// frontend/src/hooks/chat/useChatEvents.ts
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useStore } from '../../store';
import { events, type EventStreamState } from '../../services/events';
import {
  appendMessagesAfter,
  chatKeys,
  flattenMessages,
  hasFetchedHistory,
  lastFetchedSeq,
  MESSAGE_PAGE_SIZE,
  refetchInboxCoalesced,
  useMarkRead,
  type MessagePages,
} from './useChatQueries';
import { invalidateAttachmentUrl } from './useAttachmentUrls';

/**
 * Turns EMR-1874 frames into cache work. A frame carries ids only, so nothing
 * here renders a frame: the body always arrives through the normal API.
 * Mounted once, by ChatLauncher.
 */
export function useChatEvents(): void {
  const queryClient = useQueryClient();
  const markRead = useMarkRead();
  // Read through a ref: the subscription is registered once, and re-registering
  // it on every channel change would drop frames between the two.
  const openChannelRef = useRef<string | null>(null);
  const activeChannelId = useStore(state => state.activeChannelId);
  const chatView = useStore(state => state.chatView);
  const isChatOpen = useStore(state => state.isChatOpen);
  // A closed panel keeps no visible channel, whatever the view says: a channel
  // the person cannot see must not be marked read.
  openChannelRef.current =
    isChatOpen && chatView === 'channel' ? activeChannelId : null;

  useEffect(() => {
    // The cursor skips rows that only a send response cached, so a message
    // another person sent just before that send is still fetched.
    const lastSeq = (channelId: string): number =>
      lastFetchedSeq(
        queryClient,
        channelId,
        flattenMessages(
          (
            queryClient.getQueryData(
              chatKeys.messages(channelId)
            ) as MessagePages
          )?.pages
        )
      );

    // One catch-up per channel. A frame that lands while it runs sets `again`,
    // so the loop fetches once more instead of starting a parallel fetch with
    // the same cursor, and a burst larger than a page drains page by page.
    const catchUps = new Map<string, { again: boolean }>();
    // Channels whose frame landed before their first history page loaded. That
    // page may have been built before the row committed, so the catch-up runs
    // once the page is cached.
    const waitingForHistory = new Set<string>();
    const catchUp = async (channelId: string): Promise<void> => {
      const running = catchUps.get(channelId);
      if (running) {
        running.again = true;
        return;
      }
      const state = { again: false };
      catchUps.set(channelId, state);
      try {
        for (;;) {
          state.again = false;
          // Until the first history page loads, `after=0` would drain the
          // channel from its oldest row. That load brings the newest page, and
          // ChatChannel marks read from it. After it, `after=0` means the
          // channel had no fetched row, so it brings exactly the first rows.
          if (!hasFetchedHistory(queryClient, channelId)) {
            waitingForHistory.add(channelId);
            return;
          }
          const after = lastSeq(channelId);
          const count = await appendMessagesAfter(
            queryClient,
            channelId,
            after
          );
          if (count < MESSAGE_PAGE_SIZE && !state.again) break;
        }
      } finally {
        catchUps.delete(channelId);
      }
      // Re-checked after the last page: the person may have closed the panel,
      // left the channel or hidden the tab while the fetch ran. An empty
      // channel whose fetch brought nothing has no seq to mark read.
      const seq = lastSeq(channelId);
      if (
        seq > 0 &&
        openChannelRef.current === channelId &&
        document.visibilityState === 'visible'
      ) {
        markRead(channelId, seq);
        return;
      }
      // The read was withheld, so the row stays unread and mark-read's own
      // refetch never runs. Refresh the inbox here instead, or the launcher
      // badge, the tab title and the inbox row keep showing no unread for a
      // message the person never saw.
      refetchInboxCoalesced(queryClient);
    };

    const unsubscribeHistory = queryClient.getQueryCache().subscribe(event => {
      if (waitingForHistory.size === 0) return;
      if (event.type !== 'updated' || event.action.type !== 'success') return;
      for (const channelId of waitingForHistory) {
        if (!hasFetchedHistory(queryClient, channelId)) continue;
        waitingForHistory.delete(channelId);
        // The catch-up re-checks the panel and the tab before it marks read.
        void catchUp(channelId).catch(error =>
          console.error('Chat live fetch failed', error)
        );
      }
    });

    const unsubscribeMessage = events.subscribe('chat.message', frame => {
      const channelId = frame.payload.channel_id;
      const isOpen = openChannelRef.current === channelId;
      const isVisible = document.visibilityState === 'visible';

      if (isOpen && isVisible) {
        // No inbox refetch here: one that starts before the mark-read POST
        // commits brings back unread_count 1. Mark-read refetches the inbox
        // after it succeeds, which also brings the new preview for Back.
        void catchUp(channelId).catch(error =>
          console.error('Chat live fetch failed', error)
        );
        return;
      }
      refetchInboxCoalesced(queryClient);
    });

    // A preview is ready: drop the cached URL pair (it had no preview_url) and
    // refetch the history, so the message carries status 'ready'.
    const unsubscribeAttachment = events.subscribe('chat.attachment', frame => {
      invalidateAttachmentUrl(frame.payload.attachment_id);
      void queryClient.invalidateQueries({
        queryKey: chatKeys.messages(frame.payload.channel_id),
      });
    });

    const unsubscribeReset = events.onReset(() => {
      void queryClient.invalidateQueries({ queryKey: chatKeys.inbox() });
      if (openChannelRef.current) {
        void queryClient.invalidateQueries({
          queryKey: chatKeys.messages(openChannelRef.current),
        });
      }
    });

    // Same pattern as the /auth/me visibility refetch in App.tsx.
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      void queryClient.invalidateQueries({ queryKey: chatKeys.inbox() });
      const channelId = openChannelRef.current;
      if (channelId) {
        void queryClient.invalidateQueries({
          queryKey: chatKeys.messages(channelId),
        });
        // A read the catch-up withheld while the tab was hidden is still owed:
        // those rows are already cached, so nothing else would ever send it.
        // `markRead` re-checks the tab and the open panel for itself.
        const seq = lastSeq(channelId);
        if (seq > 0) {
          markRead(channelId, seq);
        }
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      unsubscribeHistory();
      unsubscribeMessage();
      unsubscribeAttachment();
      unsubscribeReset();
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [queryClient, markRead]);
}

/**
 * The wrapper publishes `state` as a property, not a subscription, so the strip
 * samples it while the panel is open.
 */
export function useEventsConnectionState(): EventStreamState {
  const isChatOpen = useStore(state => state.isChatOpen);
  const [state, setState] = useState<EventStreamState>(events.state);
  useEffect(() => {
    if (!isChatOpen) return;
    setState(events.state);
    const id = setInterval(() => setState(events.state), 1000);
    return () => clearInterval(id);
  }, [isChatOpen]);
  return state;
}
